import { useTranslation } from 'react-i18next';
import {
  RiBroadcastLine,
  RiCheckLine,
  RiCodeSSlashLine,
  RiCpuLine,
  RiErrorWarningLine,
  RiFileExcel2Line,
  RiLoader2Fill,
  RiRouterLine,
  RiServerLine,
  RiShutDownLine,
  RiWebhookLine,
  type RemixiconComponentType,
} from '@remixicon/react';
import type { ConnectorDto, ConnectorScope, ConnectorStatus, ConnectorType } from '@atnt/shared';
import { cn } from '@/shared/lib/cn';
import { Badge, IconChip, Tooltip, type BadgeColor, type ChipColor, type IconChipSize, type IconLike } from '@/shared/ui';
import { formatReadingTime } from '@/features/devices/format';

/** Icon / chip colour of every connector type; `soon` = the API has no driver yet ("Tez orada", configurable in the mock). */
export const TYPE_META: Record<ConnectorType, { icon: RemixiconComponentType; color: ChipColor; soon: boolean }> = {
  atnt_unified: { icon: RiServerLine, color: 'pink', soon: false },
  rest: { icon: RiCodeSSlashLine, color: 'darkBlue', soon: false },
  mqtt: { icon: RiBroadcastLine, color: 'purple', soon: true },
  opcua: { icon: RiCpuLine, color: 'orange', soon: true },
  modbus_tcp: { icon: RiRouterLine, color: 'darkGreen', soon: true },
  webhook: { icon: RiWebhookLine, color: 'blue', soon: true },
  file_import: { icon: RiFileExcel2Line, color: 'o5fcd3f', soon: true },
};

export function TypeChip({ type, size = 28 }: { type: ConnectorType; size?: IconChipSize }) {
  const meta = TYPE_META[type];
  return <IconChip icon={meta.icon} color={meta.color} size={size} className={size <= 20 ? 'rounded-4 [&>svg]:size-12' : size <= 28 ? '[&>svg]:size-15' : undefined} />;
}

const STATUS: Record<ConnectorStatus, { color: BadgeColor; icon: IconLike }> = {
  connected: { color: 'green', icon: RiCheckLine },
  error: { color: 'red', icon: RiErrorWarningLine },
  disabled: { color: 'neutral', icon: RiShutDownLine },
  checking: { color: 'blue', icon: <RiLoader2Fill aria-hidden className="size-16 shrink-0 animate-spin" /> },
};

export function ConnectorStatusBadge({ status, size }: { status: ConnectorStatus; size?: 'sm' | 'md' }) {
  const { t } = useTranslation('connectors');
  const s = STATUS[status];
  return (
    <Badge color={s.color} size={size} leftIcon={s.icon}>
      {t(`statuses.${status}`)}
    </Badge>
  );
}

export function ScopeBadge({ scope }: { scope: ConnectorScope }) {
  const { t } = useTranslation('connectors');
  return <Badge color={scope === 'organization' ? 'violet' : 'base-blue'}>{t(`scopes.${scope}`)}</Badge>;
}

/** "12 / 31" with a thin progress line. */
export function BoundCount({ c }: { c: Pick<ConnectorDto, 'sensorsBound' | 'sensorsTotal'> }) {
  const pct = c.sensorsTotal ? Math.round((c.sensorsBound / c.sensorsTotal) * 100) : 0;
  return (
    <span className="flex min-w-0 flex-1 items-center gap-10">
      <span className="shrink-0 tabular-nums">
        {c.sensorsBound}
        <span className="text-fg-tertiary"> / {c.sensorsTotal}</span>
      </span>
      <span aria-hidden className="h-4 max-w-80 min-w-24 flex-1 overflow-hidden rounded-full bg-field">
        <span className="block h-full rounded-full bg-accent-2" style={{ width: `${pct}%` }} />
      </span>
    </span>
  );
}

/** "5 daqiqa oldin" etc. */
export function useRelativeTime() {
  const { t } = useTranslation('connectors');
  return (ts: string | null | undefined, now = Date.now()) => {
    if (!ts) return t('time.never');
    const sec = Math.max(0, Math.round((now - Date.parse(ts)) / 1000));
    if (sec < 60) return t('time.justNow');
    const min = Math.round(sec / 60);
    if (min < 60) return t('time.minutes', { count: min });
    const h = Math.round(min / 60);
    if (h < 48) return t('time.hours', { count: h });
    return t('time.days', { count: Math.round(h / 24) });
  };
}

/** Relative time with the exact time in a tooltip. */
export function RelativeTime({ ts, className }: { ts: string | null | undefined; className?: string }) {
  const rel = useRelativeTime();
  if (!ts) return <span className={cn('text-fg-tertiary', className)}>{rel(ts)}</span>;
  return (
    <Tooltip content={formatReadingTime(ts)}>
      <span className={cn('truncate tabular-nums', className)}>{rel(ts)}</span>
    </Tooltip>
  );
}
